import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Card,
  Statistic,
  DatePicker,
  Select,
  Button,
  Table,
  Progress,
  Tag,
  Space,
  Input,
  Tooltip,
  message,
  Grid
} from "antd";
import {
  DollarOutlined,
  ShoppingOutlined,
  RiseOutlined,
  CalendarOutlined,
  BookOutlined,
  DownloadOutlined,
  SearchOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

import "./style.scss";
import { getAllPayments, getAllCoursesByRole } from "../../../Constants/Api/functions/function";

dayjs.extend(isBetween);

const { RangePicker } = DatePicker;

// Reusable Custom SVG Chart Component matching the design guidelines
const RevenueTrendChart = ({ data, strokeColor, gradientId, gradientColors }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  if (!data || data.length === 0) {
    return (
      <div className="custom-chart-card empty">
        <h3>Revenue Growth Trend</h3>
        <p>No successful payments found in this timeframe.</p>
      </div>
    );
  }

  const width = 600;
  const height = 300;
  const padding = { top: 25, right: 20, bottom: 40, left: 65 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const maxVal = Math.max(...data.map(d => Number(d.value) || 0), 1000);

  const points = data.map((d, index) => {
    const x = padding.left + (index / Math.max(data.length - 1, 1)) * chartWidth;
    const val = Number(d.value) || 0;
    const yCoord = padding.top + chartHeight - (val / maxVal) * chartHeight;
    return { x, yCoord, value: val, label: d.label };
  });

  let pathD = "";
  let areaD = "";
  if (points.length > 0) {
    pathD = `M ${points[0].x} ${points[0].yCoord}`;
    for (let i = 1; i < points.length; i++) {
      pathD += ` L ${points[i].x} ${points[i].yCoord}`;
    }
    areaD = `${pathD} L ${points[points.length - 1].x} ${padding.top + chartHeight} L ${points[0].x} ${padding.top + chartHeight} Z`;
  }

  const gridLines = [];
  for (let i = 0; i <= 4; i++) {
    const val = (maxVal / 4) * i;
    const yCoord = padding.top + chartHeight - (val / maxVal) * chartHeight;
    gridLines.push({ val, yCoord });
  }

  const formatYValue = (val) => {
    if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `${(val / 1000).toFixed(0)}k`;
    return val;
  };

  return (
    <div className="revenue-trend-chart-card">
      <div className="chart-header">
        <div>
          <h3>Revenue Growth Trend</h3>
          <p className="subtitle">Visual representation of revenue performance</p>
        </div>
        {hoveredIndex !== null ? (
          <div className="chart-tooltip-indicator" style={{ color: strokeColor }}>
            <span className="tooltip-label">{points[hoveredIndex].label}: </span>
            <span className="tooltip-value">LKR {points[hoveredIndex].value.toLocaleString()}</span>
          </div>
        ) : (
          <div className="chart-summary-indicator">
            <span className="tooltip-label">Average: </span>
            <span className="tooltip-value">
              LKR {Math.round(data.reduce((sum, d) => sum + Number(d.value), 0) / data.length).toLocaleString()}
            </span>
          </div>
        )}
      </div>

      <div className="chart-svg-container">
        <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={gradientColors[0]} stopOpacity="0.4" />
              <stop offset="100%" stopColor={gradientColors[1]} stopOpacity="0.0" />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          {gridLines.map((line, idx) => (
            <g key={idx}>
              <line x1={padding.left} y1={line.yCoord} x2={width - padding.right} y2={line.yCoord} stroke="#f3f4f6" strokeDasharray="4 4" />
              <text x={padding.left - 10} y={line.yCoord + 4} textAnchor="end" fill="#9ca3af" fontSize="10" fontWeight="500">
                {formatYValue(line.val)}
              </text>
            </g>
          ))}

          {/* Base line */}
          <line x1={padding.left} y1={padding.top + chartHeight} x2={width - padding.right} y2={padding.top + chartHeight} stroke="#e5e7eb" />

          {/* Area under the line */}
          {areaD && <path d={areaD} fill={`url(#${gradientId})`} />}

          {/* Core line */}
          {pathD && <path d={pathD} fill="none" stroke={strokeColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />}

          {/* Tooltip hover trigger blocks */}
          {points.map((p, idx) => (
            <g key={idx}>
              <text x={p.x} y={padding.top + chartHeight + 18} textAnchor="middle" fill="#9ca3af" fontSize="9" fontWeight="600">
                {p.label}
              </text>
              <circle cx={p.x} cy={p.yCoord} r={hoveredIndex === idx ? 6 : 4} fill={strokeColor} stroke="#ffffff" strokeWidth="2" style={{ transition: "all 0.15s ease-out" }} />
            </g>
          ))}

          {points.map((p, idx) => {
            const colWidth = chartWidth / (points.length - 1 || 1);
            return (
              <rect
                key={idx}
                x={p.x - colWidth / 2}
                y={padding.top}
                width={colWidth}
                height={chartHeight}
                fill="transparent"
                style={{ cursor: "pointer" }}
                onMouseEnter={() => setHoveredIndex(idx)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
            );
          })}
        </svg>
      </div>
    </div>
  );
};

const RevenueReport = () => {
  const userProfile = useSelector((state) => state.userProfile.userProfile);
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [courses, setCourses] = useState([]);

  // Filters State
  const [dateRange, setDateRange] = useState([dayjs().subtract(30, "day"), dayjs()]);
  const [selectedCourse, setSelectedCourse] = useState(undefined);
  const [txSearchText, setTxSearchText] = useState("");

  // Excel Loading State
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [paymentsList, coursesList] = await Promise.all([
        getAllPayments(),
        getAllCoursesByRole("superadmin", userProfile?.id)
      ]);
      setPayments(paymentsList || []);
      setCourses(coursesList || []);
    } catch (err) {
      console.error("Failed to load revenue details:", err);
      message.error("Failed to fetch payments data.");
    } finally {
      setLoading(false);
    }
  };

  // Filter payments list according to selection criteria
  const getFilteredPayments = () => {
    return payments.filter((p) => {
      // 1. Success/Paid/Completed statuses only
      const status = p.paymentStatus?.toLowerCase();
      const isSuccessful = status === "success" || status === "paid" || status === "completed";
      if (!isSuccessful) return false;

      // 2. Course Filter
      if (selectedCourse && p.courseId !== selectedCourse) return false;

      // 3. Date Range Filter
      if (dateRange && dateRange[0] && dateRange[1]) {
        const payDate = dayjs(p.paymentDate || p.createdAt);
        const start = dateRange[0].startOf("day");
        const end = dateRange[1].endOf("day");
        if (!payDate.isBetween(start, end, null, "[]")) return false;
      }

      return true;
    });
  };

  const filteredPayments = getFilteredPayments();

  // Summary KPIs
  const totalRevenue = filteredPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const totalTransactions = filteredPayments.length;
  const avgTransaction = totalTransactions > 0 ? totalRevenue / totalTransactions : 0;

  // Group Course breakdown stats
  const getCourseBreakdown = () => {
    const courseStats = {};
    
    // Initialize stats for all courses to show 0s if they exist
    courses.forEach(c => {
      courseStats[c.id] = {
        courseId: c.id,
        courseName: c.name,
        sales: 0,
        revenue: 0
      };
    });

    // Aggregate payments
    filteredPayments.forEach((p) => {
      if (!courseStats[p.courseId]) {
        courseStats[p.courseId] = {
          courseId: p.courseId,
          courseName: p.courseName || "Unknown Course",
          sales: 0,
          revenue: 0
        };
      }
      courseStats[p.courseId].sales += 1;
      courseStats[p.courseId].revenue += parseFloat(p.amount || 0);
    });

    return Object.values(courseStats).sort((a, b) => b.revenue - a.revenue);
  };

  const courseBreakdown = getCourseBreakdown();
  const topCourse = courseBreakdown.length > 0 && courseBreakdown[0].revenue > 0 
    ? courseBreakdown[0].courseName 
    : "N/A";

  const getPaymentMethodsBreakdown = () => {
    const methods = {};
    filteredPayments.forEach((p) => {
      const method = p.paymentMethod || "Online Checkout";
      if (!methods[method]) {
        methods[method] = {
          name: method,
          sales: 0,
          revenue: 0
        };
      }
      methods[method].sales += 1;
      methods[method].revenue += parseFloat(p.amount || 0);
    });
    return Object.values(methods).sort((a, b) => b.revenue - a.revenue);
  };

  const paymentMethodsBreakdown = getPaymentMethodsBreakdown();

  // Chart Data Generation (Growth Visuals)
  const getChartData = () => {
    if (!dateRange || !dateRange[0] || !dateRange[1] || filteredPayments.length === 0) return [];

    const start = dateRange[0];
    const end = dateRange[1];
    const diffDays = end.diff(start, "day");

    // Grouping by Date/Month depending on range length
    const formatStr = diffDays <= 45 ? "YYYY-MM-DD" : "MMM YYYY";
    const labelFormatStr = diffDays <= 45 ? "DD MMM" : "MMM YY";
    
    const groups = {};
    
    // Sort payments chronologically first
    const sortedPayments = [...filteredPayments].sort((a, b) => 
      dayjs(a.paymentDate || a.createdAt).unix() - dayjs(b.paymentDate || b.createdAt).unix()
    );

    sortedPayments.forEach((p) => {
      const pDate = dayjs(p.paymentDate || p.createdAt);
      const groupKey = pDate.format(formatStr);
      const label = pDate.format(labelFormatStr);

      if (!groups[groupKey]) {
        groups[groupKey] = {
          key: groupKey,
          label: label,
          value: 0
        };
      }
      groups[groupKey].value += parseFloat(p.amount || 0);
    });

    return Object.values(groups);
  };

  const chartDataPoints = getChartData();

  // Excel Exporter
  const handleExportExcel = () => {
    if (filteredPayments.length === 0) {
      message.warning("No data available to export in this timeframe.");
      return;
    }
    setIsExporting(true);

    // 1. Prepare Summary Sheet data
    const summarySheetData = courseBreakdown.map((item) => ({
      "Course Name": item.courseName,
      "Enrollments (Sales)": item.sales,
      "Revenue (LKR)": item.revenue,
      "Contribution %": totalRevenue > 0 ? ((item.revenue / totalRevenue) * 100).toFixed(2) + "%" : "0%"
    }));

    // 2. Prepare Transactions Sheet data
    const transactionSheetData = filteredPayments.map((p) => ({
      "Transaction ID": p.id,
      "Student Name": p.userName || "N/A",
      "Email": p.email || "N/A",
      "Course Name": p.courseName || "Unknown Course",
      "Payment Method": p.paymentMethod || "Online Checkout",
      "Amount (LKR)": parseFloat(p.amount || 0),
      "Payment Date": p.paymentDate ? dayjs(p.paymentDate).format("YYYY-MM-DD HH:mm:ss") : dayjs(p.createdAt).format("YYYY-MM-DD HH:mm:ss"),
      "Status": p.paymentStatus
    }));

    // Dynamic Imports matching project guidelines
    Promise.all([
      import("xlsx"),
      import("file-saver")
    ]).then(([xlsx, FileSaver]) => {
      const wb = xlsx.utils.book_new();

      // Summary sheet
      const wsSummary = xlsx.utils.json_to_sheet(summarySheetData);
      xlsx.utils.book_append_sheet(wb, wsSummary, "Revenue Summary");

      // Transactions sheet
      const wsTx = xlsx.utils.json_to_sheet(transactionSheetData);
      xlsx.utils.book_append_sheet(wb, wsTx, "Transaction Log");

      // Write array buffer
      const excelBuffer = xlsx.write(wb, { bookType: "xlsx", type: "array" });
      const EXCEL_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      const blob = new Blob([excelBuffer], { type: EXCEL_TYPE });
      
      const fileName = `Revenue_Report_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`;
      FileSaver.default.saveAs(blob, fileName);
      
      message.success("Excel report exported successfully!");
    }).catch(err => {
      console.error("Export error", err);
      message.error("Failed to generate Excel report.");
    }).finally(() => {
      setIsExporting(false);
    });
  };

  // Preset Filters
  const handleRangePreset = (days) => {
    setDateRange([dayjs().subtract(days, "day"), dayjs()]);
  };

  // Detailed payments table filtering by search input
  const getSearchedPayments = () => {
    if (!txSearchText.trim()) return filteredPayments;
    const s = txSearchText.toLowerCase();
    return filteredPayments.filter(p => 
      (p.userName || "").toLowerCase().includes(s) ||
      (p.email || "").toLowerCase().includes(s) ||
      (p.courseName || "").toLowerCase().includes(s) ||
      (p.paymentMethod || "").toLowerCase().includes(s)
    );
  };

  const searchedPaymentsList = getSearchedPayments();

  // Columns for course breakdown table
  const breakdownColumns = [
    {
      title: "Course Title",
      dataIndex: "courseName",
      key: "courseName",
      render: (text) => <strong>{text}</strong>
    },
    {
      title: "Completed Sales",
      dataIndex: "sales",
      key: "sales",
      align: "center",
      render: (val) => <Tag color="blue" style={{ fontSize: "12px", padding: "2px 8px" }}>{val} Enrolled</Tag>
    },
    {
      title: "Revenue (LKR)",
      dataIndex: "revenue",
      key: "revenue",
      render: (val) => <span className="revenue-val">{val.toLocaleString()} LKR</span>
    },
    {
      title: "Contribution",
      key: "contribution",
      width: "25%",
      render: (_, record) => {
        const pct = totalRevenue > 0 ? (record.revenue / totalRevenue) * 100 : 0;
        return (
          <div className="progress-cell">
            <Progress percent={parseFloat(pct.toFixed(1))} size="small" strokeColor="#f0b323" />
          </div>
        );
      }
    }
  ];

  // Columns for individual transaction log table
  const txColumns = [
    {
      title: "Student Details",
      key: "student",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.userName}</div>
          <div style={{ fontSize: "11px", color: "#666" }}>{record.email}</div>
        </div>
      )
    },
    {
      title: "Course",
      dataIndex: "courseName",
      key: "courseName",
      ellipsis: true
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => <strong>{parseFloat(amount).toLocaleString()} LKR</strong>
    },
    {
      title: "Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      render: (method) => <Tag color="geekblue">{method || "Online Checkout"}</Tag>
    },
    {
      title: "Date / Time",
      key: "date",
      render: (_, record) => (
        <span>{dayjs(record.paymentDate || record.createdAt).format("YYYY-MM-DD HH:mm")}</span>
      )
    }
  ];

  return (
    <div className="revenue-report-page">
      <div className="revenue-header">
        <div>
          <h2>System Revenue Analytics</h2>
          <p>Monitor earnings, sales performance, and visual growth trends across all courses.</p>
        </div>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleExportExcel}
          loading={isExporting}
          className="export-btn-premium"
        >
          Export Excel Report
        </Button>
      </div>

      {/* FILTER CONTROL CARD */}
      <Card className="glass-card filter-card">
        <Space direction={screens.xs ? "vertical" : "horizontal"} size="middle" wrap style={{ width: "100%" }}>
          <div className="filter-group">
            <span className="filter-label"><CalendarOutlined /> Timeframe:</span>
            <RangePicker
              value={dateRange}
              onChange={(val) => setDateRange(val)}
              allowClear={false}
              style={{ borderRadius: "6px" }}
            />
          </div>

          <div className="preset-buttons">
            <Button size="small" onClick={() => handleRangePreset(7)}>7 Days</Button>
            <Button size="small" onClick={() => handleRangePreset(30)}>30 Days</Button>
            <Button size="small" onClick={() => handleRangePreset(180)}>6 Months</Button>
            <Button size="small" onClick={() => setDateRange([dayjs().startOf("year"), dayjs()])}>This Year</Button>
          </div>

          <div className="filter-group" style={{ marginLeft: screens.xs ? 0 : "auto" }}>
            <span className="filter-label"><BookOutlined /> Course Filter:</span>
            <Select
              allowClear
              placeholder="All Courses"
              value={selectedCourse}
              onChange={(val) => setSelectedCourse(val)}
              style={{ width: 220, borderRadius: "6px" }}
              dropdownMatchSelectWidth={false}
            >
              {courses.map((c) => (
                <Select.Option key={c.id} value={c.id}>
                  {c.name}
                </Select.Option>
              ))}
            </Select>
          </div>
        </Space>
      </Card>

      {/* KPI METRIC CARDS */}
      <div className="revenue-kpi-grid">
        <Card className="glass-kpi gold">
          <Statistic
            title="Total Net Revenue"
            value={totalRevenue}
            precision={2}
            suffix="LKR"
            prefix={<DollarOutlined className="kpi-icon" />}
            loading={loading}
          />
        </Card>

        <Card className="glass-kpi blue">
          <Statistic
            title="Successful Registrations"
            value={totalTransactions}
            suffix="Enrollments"
            prefix={<ShoppingOutlined className="kpi-icon" />}
            loading={loading}
          />
        </Card>

        <Card className="glass-kpi purple">
          <Statistic
            title="Top Performing Course"
            value={topCourse}
            formatter={(value) => (
              <Tooltip title={value}>
                <div className="kpi-top-course-text">{value}</div>
              </Tooltip>
            )}
            loading={loading}
          />
        </Card>
      </div>

      {/* GROWTH CHART & PAYMENT METHOD VISUALS */}
      <div className="chart-grid-row">
        <RevenueTrendChart
          data={chartDataPoints}
          strokeColor="#f0b323"
          gradientId="revenueGrad"
          gradientColors={["#f0b323", "#ffffff"]}
        />

        {/* REVENUE BY PAYMENT METHOD */}
        <Card className="glass-card payment-methods-card" title="Revenue by Payment Method" bordered={false}>
          <div className="payment-methods-list">
            {paymentMethodsBreakdown.map((item, idx) => {
              const pct = totalRevenue > 0 ? (item.revenue / totalRevenue) * 100 : 0;
              // Preset colors for payment method indicators
              const colors = ["#1e40af", "#0d9488", "#7c3aed", "#f0b323"];
              const currentColor = colors[idx % colors.length];

              // Map 'paycorp' to 'Card Payment (Sampath Gateway)'
              const displayName = item.name.toLowerCase() === "paycorp"
                ? "Card Payment (Sampath Gateway)"
                : item.name;

              return (
                <div key={item.name} className="payment-method-item">
                  <div className="item-header">
                    <span className="method-name">
                      <span className="color-dot" style={{ backgroundColor: currentColor }}></span>
                      <strong>{displayName}</strong>
                    </span>
                    <span className="method-sales">
                      <Tag color="default">{item.sales} Sales</Tag>
                    </span>
                  </div>
                  <div className="item-body">
                    <div className="item-amount">
                      <span>LKR {item.revenue.toLocaleString()}</span>
                      <span className="pct-label">{pct.toFixed(1)}%</span>
                    </div>
                    <Progress 
                      percent={parseFloat(pct.toFixed(1))} 
                      showInfo={false} 
                      strokeColor={currentColor} 
                      size="small" 
                    />
                  </div>
                </div>
              );
            })}
            {paymentMethodsBreakdown.length === 0 && (
              <p className="empty-text">No payment records available.</p>
            )}
          </div>
        </Card>
      </div>

      {/* BREAKDOWN SECTIONS */}
      <div className="breakdown-grid">
        {/* COURSE BREAKDOWN TABLE */}
        <Card className="glass-card list-card" title="Revenue Share by Course" bordered={false}>
          <Table
            dataSource={courseBreakdown}
            columns={breakdownColumns}
            rowKey="courseId"
            loading={loading}
            pagination={{ pageSize: 5 }}
            className="glass-table"
          />
        </Card>

        {/* DETAILED TRANSACTION LOGS */}
        <Card
          className="glass-card list-card"
          title="Recent Transactions Log"
          bordered={false}
          extra={
            <Input
              placeholder="Search student, course, method..."
              prefix={<SearchOutlined />}
              value={txSearchText}
              onChange={(e) => setTxSearchText(e.target.value)}
              style={{ width: 240, borderRadius: "6px" }}
              allowClear
            />
          }
        >
          <Table
            dataSource={searchedPaymentsList}
            columns={txColumns}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 5 }}
            className="glass-table"
            scroll={{ x: "max-content" }}
          />
        </Card>
      </div>
    </div>
  );
};

export default RevenueReport;
